import { axiosInstance } from '../../Authentication/AxiosInterceptor'; // Adjust the import path
import { ImprovementPlanDisplayInfo } from '../../Types/AreaDisplayInfo/ImprovementPlanDisplayInfo';
import { CreateEvaluateImprovementRequest } from '../../Types/Evaluate/CreateEvaluateImprovementRequest';
import { CreateDevelopImprovementRequest } from '../../Types/Develop/CreateDevelopImprovementRequest';
import { ImprovementPlanDetail, ImprovementDetail, ImprovementPlanDetailResponse, ImproveTaskDetail, ImprovementPlanProgress } from '../../Types/Improve/ImprovementPlanDetail'; // Create and adjust path to types if needed
import { PagedResponse } from '../../Types/Paging/PagedResponse';
import { AnalyseDataRequest } from '../../Types/Paging/PagedRequest';
import { ImproveTemplateDto } from '../../Types/Improve/ImproveTemplateDto';
import { AxiosResponse } from 'axios';
import { ImportTemplateRequest } from '../../Types/Evaluate/ImportTemplateRequest';
import { LinkedEvidenceResponse } from '../../Components/Utils/LinkedEvidencePopup/LinkedEvidencePopup'
import { Comment } from "../../Components/Utils/CommentsPopup/CommentsPopup";
import { ImproveAreaDisplayInfo } from '../../Types/AreaDisplayInfo/AreaDisplayInfo';
import { ProductType } from '../../Types/ProductType';
import { ImprovementWithLinks } from '../../Types/Improve/LinkedDiagnostics';
import { ImproveAreaExportData } from '../../Utils/Export/ImproveExport/types';
import { dedupeAsync } from '../../Utils/dedupeAsync';

export interface ImprovementDTO {
  parentArea: string;
  parentAreaTitle?: string;
  improvement: ImprovementWithLinks & { objective?: { id: string; title?: string } };
}

export async function ImportImproveTemplate(templateId: string, clientId: string, departmentId?: string, partnerId?: string, groupIds?: string[]): Promise<ImprovementPlanDetail>{

    try {
        const response: AxiosResponse<ImprovementPlanDetail> = await axiosInstance.post("ImportImproveTemplate", {
            templateId: templateId,
            departmentId: departmentId,
            partnerId: partnerId,
            groupIds: groupIds,
            clientId: clientId
        } as ImportTemplateRequest);

        return response.data;
       
      } catch (error) {
        console.log(error);
        throw error;
      }
}
  
export const GetImprovementPlanDetailById = async (id: string, archived: boolean): Promise<ImprovementPlanDetailResponse> => {
  try {
    const response = await axiosInstance.get<ImprovementPlanDetailResponse>(`Improve/GetImprovementPlanDetailById/${id}/${archived}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ImproveArea by ID:', error);
    throw error; // Allow caller to handle the error
  }
};

export const GetImprovementPlanProgress = async (id: string): Promise<ImprovementPlanProgress> => {
  try {
    const response = await axiosInstance.get<ImprovementPlanProgress>(`Improve/GetImprovementPlanProgress/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ImproveArea by ID:', error);
    throw error; // Allow caller to handle the error
  }
};

export const GetImprovementPlanDisplayInfoByClientAndUser = async (
  clientId: string,
  userId: string,
  productType: ProductType,
  areaContextId?: string,
): Promise<ImprovementPlanDisplayInfo[]> => {

    try {
        const contextSegment =
            areaContextId &&
            areaContextId !== 'undefined' &&
            areaContextId !== 'null'
                ? encodeURIComponent(areaContextId)
                : '-';
        const response = await axiosInstance.get<ImprovementPlanDisplayInfo[]>(
          `/GetImprovementPlanDisplayInfoByClientAndUser/${encodeURIComponent(clientId)}/${encodeURIComponent(userId)}/${productType}/${contextSegment}`,
        );
        return response.data;
        
    } catch (error) {
        console.error('Error fetching GetImprovementPlanDisplayInfoByClientAndUser:', error);
        throw error; // Handle the error properly in application logic
    }

}

export const GetImproveAreaDisplayInfosByClientAndUser = async (clientId: string, request: AnalyseDataRequest): Promise<PagedResponse<ImproveAreaDisplayInfo>> => {
    try {
        return await dedupeAsync(
            `improve-areas:${clientId}:${request.archived}:${request.pageNumber}:${request.searchTerm ?? ""}`,
            async () => {
                const response = await axiosInstance.post<PagedResponse<ImproveAreaDisplayInfo>>(
                    `Improve/GetPagedImproveAreaDisplayInfosByClientAndUser/${clientId}`,
                    request
                );
                return response.data;
            }
        );
    } catch (error) {
        console.error('Error fetching improve area display infos:', error);
        throw error;
    }
};

export async function getImproveAreasForExportByAreaIds(
    areaIds: string[],
    archived = false,
): Promise<ImproveAreaExportData[]> {
    const response = await axiosInstance.post<ImproveAreaExportData[]>(
        'Improve/GetImproveAreasForExportByAreaIds',
        { areaIds, archived },
    );
    return response.data ?? [];
}
    

export const CreateImprovementByTitle = async (areaId: string, title: string) => {
    try {
          const response = await axiosInstance.post(`Improve/CreateImprovementByTitle/${areaId}`, title, {
              headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating improvement by title:', error);
        throw error;
    }
};

export const CreateImproveTaskByTitle = async (improvementId: string, title: string) => {
    try {
        const response = await axiosInstance.post(`Improve/CreateImproveTaskByTitle/${improvementId}`, title, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating task by title:', error);
        throw error;
    }
};


// Async Action to Call the API
export const CreateEvaluateImprovement = async (improvement: CreateEvaluateImprovementRequest) => {

    try {
      const response = await axiosInstance.post(`CreateEvaluateImprovement`, improvement);
      return response.data;
    } catch (error) {
      console.error('Error creating improvements:', error);
      throw error; // Handle the error properly in application logic
    }
};

export const GetTemplate = async (templateId: string): Promise<ImproveTemplateDto> => {
  try {
    const response = await axiosInstance.get<ImproveTemplateDto>(`Improve/GetTemplate/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
};

export const UpdateImproveAreaTitle = async (areaId: string, title: string): Promise<void> => {
  try {
    await axiosInstance.post(`Improve/UpdateImproveAreaTitle/${areaId}`, title, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error('Error updating improve area title:', error);
    throw error;
  }
};

export const UpdateImprovementByDetail = async (improvementDetail: ImprovementDetail): Promise<ImprovementDetail> => {
  try {
    var newVersion = await axiosInstance.post(`Improve/UpdateImprovementByDetail`, improvementDetail);
    return newVersion.data;
  } catch (error) {
    console.error('Error updating improvement detail:', error);
    throw error;
  }
};

export const UpdateImproveTaskByDetail = async (taskDetail: ImproveTaskDetail): Promise<ImproveTaskDetail> => {
  try {
    var newVersion = await axiosInstance.post(`Improve/UpdateImproveTaskByDetail`, taskDetail);
    return newVersion.data;
  } catch (error) {
    console.error('Error updating improvement detail:', error);
    throw error;
  }
};

export const ToggleImproveAreaArchived = async (areaId: string): Promise<void> => {
  try {
    await axiosInstance.get(`Improve/ToggleImproveAreaArchived/${areaId}`);
  } catch (error) {
    console.error('Error toggling improve area archived status:', error);
    throw error;
  }
};

export const ToggleImprovementArchived = async (improvementId: string): Promise<void> => {
  try {
    console.log('Calling API endpoint:', `Improve/ToggleImprovementArchived/${improvementId}`);
    await axiosInstance.get(`Improve/ToggleImprovementArchived/${improvementId}`);
  } catch (error) {
    console.error('Error toggling improvement archived status:', error);
    throw error;
  }
};

export async function GetImproveLinkedEvidence(areaId: string, improvementId: string): Promise<LinkedEvidenceResponse> {
  try {
    const response = await axiosInstance.get(`GetImproveLinkedEvidence/${areaId}/${improvementId}`);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function GetCommentsByContextId(contextId: string): Promise<Comment[]> {
  try {
    const response = await axiosInstance.get(`GetSummariseCommentsByCriteriaId/${contextId}`);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function CheckNewImprovementVersion(improvementId: string, version: number): Promise<ImprovementDetail | null> {
  try {
    const response = await axiosInstance.get(`Improve/CheckNewImprovementVersion/${improvementId}/${version}`);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function CheckNewImproveTaskVersion(taskId: string, version: number): Promise<ImproveTaskDetail | null> {
  try {
    const response = await axiosInstance.get(`Improve/CheckNewImproveTaskVersion/${taskId}/${version}`);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const ChangeProductAreaOwner = async (areaId: string, newOwnerSub: string): Promise<void> => {
  try {
    await axiosInstance.patch(`ChangeProductAreaOwner/${areaId}`, newOwnerSub, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error('Error changing product area owner:', error);
    throw error;
  }
};

export async function GetImprovementById(improvementId: string): Promise<ImprovementWithLinks> {
  try {
    const response = await axiosInstance.get<ImprovementWithLinks | ImprovementWithLinks[]>(
      `GetImprovementById/${improvementId}`,
    );
    const payload = response.data;

    if (Array.isArray(payload)) {
      return payload[0] ?? { id: improvementId, linkedEvaluateAreas: [] };
    }

    return payload;
  } catch (error) {
    console.error('Error fetching improvement by ID:', error);
    throw error;
  }
}

export async function CreateDevelopImprovement(
  request: CreateDevelopImprovementRequest,
): Promise<ImprovementDetail> {
  const response = await axiosInstance.post<ImprovementDetail>(
    `Improve/CreateDevelopImprovement/${request.improvementPlanId}`,
    {
      developAreaId: request.developAreaId,
      title: request.title,
      questionGroups: request.questionGroups,
    },
  );
  return response.data;
}

export async function GetImprovementsByEvaluateAreaId(evaluateAreaId: string): Promise<ImprovementDTO[]> {
  try {
    const response = await axiosInstance.get<ImprovementDTO[]>(
      `GetImprovementsByEvaluateAreaId/${evaluateAreaId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching improvements by evaluate area ID:', error);
    throw error;
  }
}